import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { Users } from "lucide-react";

/**
 * College name input with live autocomplete pulled from /api/colleges/suggest.
 * Not `required` here — parent controls it. Falls back to a plain typed value
 * if no suggestion is picked, so new colleges can still be entered.
 *
 * Note: /api/colleges/suggest requires auth. Signup uses `authOptional` so
 * unauthenticated users still see suggestions once the endpoint returns [].
 */
export default function CollegeAutocomplete({
  value,
  onChange,
  placeholder = "Government Ayurveda College, Pune",
  required = false,
  testid = "college-autocomplete-input",
  authOptional = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    // Close on outside click
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const fetchSuggestions = async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/colleges/suggest", { params: { q: q || "" } });
      setSuggestions(data);
    } catch {
      if (authOptional) setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const onInput = (v) => {
    onChange(v);
    setOpen(true);
    setHighlight(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 200);
  };

  const onFocus = () => {
    setOpen(true);
    fetchSuggestions(value);
  };

  const pick = (name) => {
    onChange(name);
    setOpen(false);
    setSuggestions([]);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      pick(suggestions[highlight].name);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onInput(e.target.value)}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        required={required}
        placeholder={placeholder}
        className="bg-white"
        autoComplete="off"
      />
      {open && (suggestions.length > 0 || loading) && (
        <div
          data-testid={`${testid}-dropdown`}
          className="absolute z-50 mt-1 w-full bg-white border border-[color:var(--ayur-border)] rounded-xl shadow-lg overflow-hidden max-h-72 overflow-y-auto"
        >
          {loading && suggestions.length === 0 && (
            <div className="px-4 py-3 text-sm text-[color:var(--ayur-mute)]">Searching…</div>
          )}
          {suggestions.map((s, i) => (
            <button
              key={s.name}
              type="button"
              data-testid={`${testid}-option-${i}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(s.name)}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-3 border-t border-[color:var(--ayur-border)] first:border-t-0 ${
                highlight === i
                  ? "bg-[color:var(--ayur-cream-2)]"
                  : "hover:bg-[color:var(--ayur-cream-2)]"
              }`}
            >
              <span className="truncate text-[color:var(--ayur-ink)]">{s.name}</span>
              <span className="shrink-0 inline-flex items-center gap-1 text-xs text-[color:var(--ayur-mute)]">
                <Users className="w-3 h-3" />
                {s.count}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
